// @ts-nocheck
import type {ImageResizeInfo, ImageV3Info, Part, PartRawData, WasmDataStructure} from './utils/utils';
import {getImageResizeInfo, isCssAspectRatioSupported, nextTick, RelativeFrom, waitImageLoad} from './utils/utils';
import {Box, Vertex2d} from './utils/Vertex';
import {get, uniqueId, uniqBy, sortBy} from 'lodash';
import WorkerPromisify from './utils/WorkerPromisify';
import {createWorker} from './utils/CorsWorker';
import {blobToArrayBuffer} from './utils/toArrayBuffer';
// eslint-disable-next-line no-undef-init
let wasmWorker: WorkerPromisify = undefined;

enum ShowMode {
    'imageOnly' = 'imageOnly',
    'imageOnlyWithTools' = 'imageOnlyWithTools',
    'boxWithoutText' = 'boxWithoutText',
    'full' = 'full',
    'edit' = 'edit',
}

export class LabelCtrlServ {
    readonly imageV3Info: ImageV3Info;
    public labelAutoLayout = true;
    public rotateNumber = 0;
    private imgBlob?: Blob;
    private imgBlobUrl?: string;
    private readonly parts: PartRawData[];
    public partsMap: { [_id: string]: Part } = {};
    public readonly id: string;
    private readonly partsSortIds: string[];

    // 标红的label
    public highlightId?: string = undefined;

    // 部分关闭label显示
    public hiddenLabelsById: string[] = []

    static ShowMode = ShowMode;

    static typeClassMap(type: string): Part['type'] {
        const map = {
            label: 'label',
            figure: 'fig',
            'sub label': 'sub',
            custom_label: 'custom_label',
            part_modify: 'part_modify',
        };
        // @ts-ignore
        return map[type] || 'sub';
    }

    get imageId() {
        return this.imageV3Info.imageId;
    }

    get canAutoLayout() {
        if (!('TextEncoder' in window) || !window.WebAssembly) {
            return false;
        }
        return this.parts.length > 0;
    }

    constructor(imageV3Info: ImageV3Info,
                options: { partsPostProcess?: (parts: PartRawData[]) => PartRawData[] } = {}
    ) {
        this.id = uniqueId('image-v3-');
        this.imageV3Info = Object.freeze(imageV3Info);
        this.rotateNumber = parseInt(imageV3Info.figure.image_direction) || 0;


        let _opts = {
            // @ts-ignore
            partsPostProcess(arr) {
                return arr
            },
            ...options,
        }

        // @ts-ignore
        this.parts = Object.freeze(
            _opts.partsPostProcess([...get(this, 'imageV3Info.figure.fig') || [], ...get(this, 'imageV3Info.figure.parts') || []])
        );
        const staticSortIds: string[] = [];
        this.parts.forEach(part => {
            if (!part.id) {
                console.error('part.id is empty', part);
                return;
            }
            const _id = uniqueId(part.id + '~~');
            this.partsMap[_id] = {
                ...part,
                _id: _id,
                id: part.id,
                originalBox: Object.freeze(new Box(...part.coordinates)),
            };
            staticSortIds.push(_id);
        })
        // @ts-ignore
        this.partsSortIds = Object.freeze(staticSortIds);
    }

    get uniqParts(): Part[] {
        return sortBy(
            uniqBy(this.partsSortIds.map(id => this.partsMap[id]), 'id'),
            'code'
        )
    }

    // override
    public getImageElement(): HTMLImageElement {
        throw new Error('getImageElement not implemented');
    }

    public getImageWrapper(): HTMLElement {
        throw new Error('getImageElement not implemented');
    }

    public getFrameSizeElement(): HTMLElement {
        throw new Error('frameSizeElement not implemented');
    }

    get rawSrcUrl(): string {
        return this.imageV3Info.src;
    }

    public imageLoaded = false; // 有可能会被外界改

    public isLayoutBusy = false;

    public async init() {
        if (this.imageLoaded) {
            return;
        }

        const imgUrl = this.rawSrcUrl;

        await nextTick(); // init时image元素可能还没render出来，等待一下
        try {
            this.imgBlob = await fetch(imgUrl, {credentials: 'omit',}).then(res => res.blob());
            this.imgBlobUrl = URL.createObjectURL(this.imgBlob);
            await waitImageLoad(this.getImageElement(), this.imgBlobUrl);
        } catch (err) {
            console.error(err);
            this.labelAutoLayout = false;
            // 这里保底如果XHR失败，用原始图片src，一般不会跑到这里面
            await waitImageLoad(this.getImageElement(), imgUrl);
        }

        this.imageLoaded = true;
    }

    public imageResizeInfo?: ImageResizeInfo = undefined;

    get layerSizeCss(): Partial<CSSStyleDeclaration> {
        if (this.imageResizeInfo) {
            const overflowFixScale = this.imageResizeInfo!.overflowFixScale
            return {
                width: this.imageResizeInfo.imageRotatedWidth / this.imageResizeInfo.naturalWidth * overflowFixScale * 100 + '%',
                height: this.imageResizeInfo.imageRotatedHeight / this.imageResizeInfo.naturalHeight * overflowFixScale * 100 + '%',
                // @ts-ignore
                '-puesto-image-scale': overflowFixScale,
            }
        }
        return {
            width: '100%',
            height: '100%',
            // @ts-ignore
            '-puesto-image-scale': 1,
        }
    }

    // 第一次保证图一定能放入容器，后面再被用户旋转就不管了
    private firstTimeOverflowFixScale: number | undefined = 1;

    public reGenImageResizeInfo(): ImageResizeInfo {

        const info = getImageResizeInfo(
            this.getImageWrapper(),
            this.getImageElement().naturalWidth,
            this.getImageElement().naturalHeight,
            this.rotateNumber,
            this.firstTimeOverflowFixScale
        );
        this.firstTimeOverflowFixScale = info.overflowFixScale;
        return info;
    }

    updateWrapperStyle() {
        const wrapperStyle = {
            transformOrigin: 'center center',
            transform: `translate(${this.userOffset.x}px, ${this.userOffset.y}px) scale(${this.userScale})`,
        }
        // const reverseOverflowFixScale = this.imageResizeInfo ? (1 / this.imageResizeInfo.overflowFixScale) : 1;
        if (isCssAspectRatioSupported) {
            const aspectRatio = this.imageResizeInfo ? `${this.imageResizeInfo.naturalWidth} / ${this.imageResizeInfo.naturalHeight}` : 'unset';
            this.wrapperStyle = {
                aspectRatio,
                ...wrapperStyle,
                // maxWidth: 'unset',
                // maxHeight: 'unset'
            };
            // if(this.imageResizeInfo) {
            //     // 头碰到上框子，翻转的时候最好让图恰好放在容器里，不要过大或过小
            //     const maxSizeCss = `${100 * reverseOverflowFixScale}%`;
            //     this.wrapperStyle.maxWidth = maxSizeCss;
            //     this.wrapperStyle.maxHeight = maxSizeCss;
            // }
        } else {
            const baseScale = this.imageResizeInfo ? this.imageResizeInfo.baseScale : 1;
            this.wrapperStyle = {
                maxWidth: 'unset',
                maxHeight: 'unset',
                width: this.imageResizeInfo ? this.imageResizeInfo.naturalWidth * baseScale + 'px' : 'unset',
                height: this.imageResizeInfo ? this.imageResizeInfo.naturalHeight * baseScale + 'px' : 'unset',
                ...wrapperStyle,
            };
        }
    }


    // 仅仅在不支持 CSS Aspect Ratio 时监听
    private __resizeObserver: ResizeObserver | undefined;

    private _listenWrapResize() {
        if (this.__resizeObserver) {
            return
        }
        this.__resizeObserver = new ResizeObserver(() => {
            this.imageResizeInfo = this.reGenImageResizeInfo();
            this.updateWrapperStyle();
        });
        this.__resizeObserver.observe(this.getFrameSizeElement());
    }


    public wrapperStyle: Partial<CSSStyleDeclaration> = {}

    private async _runLayout(rotate: number) {
        this.rotateNumber = rotate % 4;
        const imageResizeInfo = this.imageResizeInfo = this.reGenImageResizeInfo();

        this.updateWrapperStyle();


        // 更新首次part位置
        this.partsSortIds.forEach(_id => {
            const part = this.partsMap[_id];
            const renderedPartBox = this.rawBoxToRenderedBox(part.originalBox);
            // 默认label贴part右边垂直剧中（假设label未测量前 16x200 宽高）
            const renderedLabelBoxSize = new Box(0, 0, 200, 16)
            // 把16px映射到原图上
            const renderedLabelBoxScaleToRawImage = renderedLabelBoxSize.scale(1 / imageResizeInfo.scale)
            const renderedLabelBox = renderedLabelBoxScaleToRawImage
                .translate(new Vertex2d(renderedPartBox.x2, renderedPartBox.y1 + renderedPartBox.height / 2 - renderedLabelBoxScaleToRawImage.height / 2))
            this.partsMap[_id] = {
                ...part,
                renderedPartBox,
                renderedLabelBox,
            }
        });

        if (!this.canAutoLayout) {
            return
        }
        if (this.labelAutoLayout) {
            await this.runWasmLayout();
        }

        if (!isCssAspectRatioSupported) {
            this._listenWrapResize();
        }
    }

    private _runLayoutTimeouter: number | undefined;
    public runLayout(rotate: number = this.rotateNumber) {
        clearTimeout(this._runLayoutTimeouter);
        if (this._isDestroyed) {
            return Promise.resolve();
        }
        this.isLayoutBusy = true;
        // @ts-ignore
        this._runLayoutTimeouter = setTimeout(() => {this.isLayoutBusy = false}, 10000);

        return this._runLayout(rotate)
            .finally(() => {
                this.isLayoutBusy = false;
                clearTimeout(this._runLayoutTimeouter);
            })
    }

    private rawBoxToRenderedBox(rawBox: Box): Box {
        if (!this.imageResizeInfo) {
            throw new Error('imageResizeInfo is not initialized')
        }
        const rotateOriginOffset = this.imageResizeInfo.rotateOriginOffset;
        return rawBox.rotateByPointOrigin(this.rotateNumber, this.imageResizeInfo.originImageCenter)
            .normalize()
            .translate(rotateOriginOffset);
    }

    public renderedBoxToRawBox(renderedBox: Box): Box {
        if (!this.imageResizeInfo) {
            throw new Error('imageResizeInfo is not initialized')
        }

        const {originImageCenter, scaledWidth, scaledHeight,} = this.imageResizeInfo;

        // 重置O点到中心 -> 以O点为中心旋转成和原图一个方向 -> 缩放到和原图一样大 -> 重置O点到左上角
        return renderedBox
            .translate(new Vertex2d(-scaledWidth / 2, -scaledHeight / 2))
            .rotateByPointOrigin(-this.rotateNumber, new Vertex2d(0, 0))
            .scale(1 / this.imageResizeInfo.scale)
            .translate(originImageCenter)
            .normalize()
    }

    public rawBoxToCss(box: Box): Partial<CSSStyleDeclaration> {
        if (!box || !this.imageResizeInfo) {
            return {}
        }
        return Box.toCss(box.toPercentage(this.imageResizeInfo, RelativeFrom.rawImage))
    }

    public renderedPartBox2Style(partBox: Box): Partial<CSSStyleDeclaration> {
        return this.rawBoxToCss(partBox)
    }

    public renderedLabelBox2Style(labelBox: Box): Partial<CSSStyleDeclaration> {
        if (!labelBox || !this.imageResizeInfo) {
            return {}
        }
        return Vertex2d.toCss(labelBox.x1y1.toPercentage(this.imageResizeInfo, RelativeFrom.rawImage))
    }

    // 以下放大缩小用户移动图片等控制部分

    public userScale = 1;
    public userOffset = new Vertex2d(0, 0);

    ctrl_enlarge() {
        if (this.userScale >= 3.5) {
            return
        }
        this.userScale += 0.1;
        this.updateWrapperStyle();
    }

    ctrl_shrink() {
        if (this.userScale <= 0.35) {
            return
        }
        this.userScale -= 0.1;
        this.updateWrapperStyle();
    }

    async ctrl_rotate() {
        this.userOffset = new Vertex2d(0, 0);
        await this.runLayout(this.rotateNumber + 1);
    }

    ctrl_move_to(toPos: Vertex2d) {
        this.userOffset = toPos;
        this.updateWrapperStyle();
    }

    // 上面是放大缩小用户移动图片等控制部分

    // 以下是wasm部分 --------------------------------------------------------------
    private calcLabelTextHW(_id: string): WasmDataStructure.HW | null {
        const labelElement = this.getImageWrapper().querySelector(`.label-box[data-part-_id="${_id}"]`) as HTMLElement;
        if (!labelElement) {
            return null
        }
        const {offsetHeight, offsetWidth,} = labelElement;
        if (!offsetHeight || !offsetWidth) {
            return null
        }
        return [offsetHeight, offsetWidth]
    }

    private getLabelsForWasm() {
        const cords: WasmDataStructure.PartCord[] = []
        const hws: WasmDataStructure.HW[] = []
        const validPartIds: string[] = [];
        this.partsSortIds.forEach(_id => {
            const part = this.partsMap[_id];
            const renderedPartBox = part.renderedPartBox as Box;
            const partCord: WasmDataStructure.PartCord = [renderedPartBox.x2, renderedPartBox.y1, renderedPartBox.x1, renderedPartBox.y2];
            const labelSize = this.calcLabelTextHW(_id);
            if (labelSize) {
                cords.push(partCord);
                hws.push(labelSize);
                validPartIds.push(_id);
            }
        })
        return {
            validPartIds,
            cords: new Float32Array(cords.flat()),
            hws: new Uint32Array(hws.flat()),
        };
    }

    private _u8arrayBuffer?: Uint8Array = undefined;

    async getImageUnit8ArrayBuffer(): Promise<Uint8Array> {
        if (!this._u8arrayBuffer) {
            const bf = await blobToArrayBuffer(this.imgBlob!);
            this._u8arrayBuffer = new Uint8Array(bf);
        }
        return this._u8arrayBuffer;
    }

    private _currentWasmParallelId = '';
    private _cachedLayoutResult: Map<string, { partIds: string[], arr: WasmDataStructure.Result }> = new Map();

    public async runWasmLayout() {
        if (!this.imgBlob) {
            throw new Error('imgBlob not found, image not loaded?')
        }

        if (!wasmWorker) {
            wasmWorker = new WorkerPromisify<{ partIds: string[], arr: WasmDataStructure.Result }>(() => {
                return createWorker()
            })
            wasmWorker.onAnyResults = (result: { partIds: string[], arr: WasmDataStructure.Result }, parallelId: string) => {
                this._cachedLayoutResult.set(parallelId, result);
            }
        }

        const u8buffer = await this.getImageUnit8ArrayBuffer();

        this.imageResizeInfo = this.reGenImageResizeInfo();
        const {cords, hws, validPartIds} = this.getLabelsForWasm();
        if (validPartIds.length === 0) {
            console.warn('no valid label found, skip layout')
            return
        }

        const params = [
            u8buffer,
            // @ts-ignore
            parseInt(this.rotateNumber),
            cords,
            hws,
            this.imageResizeInfo.scale
        ]

        // 优先用缓存的结果
        this._currentWasmParallelId = `${this.rawSrcUrl}_${this.rotateNumber}_${validPartIds.join(',')}_${this.imageResizeInfo.scale}`;
        if (!this._cachedLayoutResult.has(this._currentWasmParallelId)) {
            const result = await wasmWorker.postMessage({
                wasmUrl: (new URL('./wasm-pkg/smart_label_rs_bg.wasm', import.meta.url)).toString(),
                params, partIds: validPartIds,
            }, () => this._currentWasmParallelId);
            this._cachedLayoutResult.set(this._currentWasmParallelId, result);
        }
        this.setWasmPositionResult(this._cachedLayoutResult.get(this._currentWasmParallelId)!);
    }

    private setWasmPositionResult(result: { partIds: string[], arr: WasmDataStructure.Result }) {
        if (this.imageResizeInfo === undefined) {
            return
        }
        const {partIds, arr} = result;
        const arr_x2y1x1y2_flat = arr;
        partIds.forEach((_id, index) => {
            const startCursor = index * 4;
            const [x2, y1, x1, y2] = [
                arr_x2y1x1y2_flat[startCursor],
                arr_x2y1x1y2_flat[startCursor + 1],
                arr_x2y1x1y2_flat[startCursor + 2],
                arr_x2y1x1y2_flat[startCursor + 3]
            ];
            const scaleToRawImage = 1 / this.imageResizeInfo!.scale;
            const resultBox = new Box(x1, y1, x2, y2)
                .normalize()
                .scale(scaleToRawImage)

            this.partsMap[_id] = {
                ...this.partsMap[_id],
                renderedLabelBox: resultBox,
            }
        })
    }

    // 以上是wasm部分 --------------------------------------------------------------

    private _isDestroyed = false;

    public destroy() {
        if (this.__resizeObserver) {
            this.__resizeObserver.disconnect();
        }
        if (this.imgBlobUrl) {
            URL.revokeObjectURL(this.imgBlobUrl);
        }
        this._isDestroyed = true;
    }
}
